import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref, watch } from 'vue'

  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import ProfileQuestion from '@/components/ProfileQuestion.vue'

  import { useRouter } from 'vue-router/composables'
  import useIcons from '@/composables/useIcons'
  import useSegment from '@/composables/useSegment'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useAuthentication from '@/composables/useAuthentication'
  import useToursAndHints from '@/composables/useToursAndHints'
  import useI18n from '@/composables/useI18n'

  import UsersApi from '@/services/api/UsersApi'

  import { UserModule } from '@/store/modules/user'

  import type { RouteRecordPublic } from 'vue-router'
  import type { CompanyProfileQuestion, StructuredCompanyProfileQuestion } from '@/services/interfaces/Auth0'
  import type { QuestionAnswer } from '@/services/interfaces/QuizQuestion'

  import CookieHelper from '@/helpers/CookieHelper'
  import { SEEN_PAGE_TOUR, CLOSED_PAGE_HINT } from '@/helpers/Constants'

  import eventBus from '@/main'

  interface ExtendedProfileQuestion extends StructuredCompanyProfileQuestion {
    phoneAnswer?: string
    textAnswer?: string
  }

  
export default /*#__PURE__*/_defineComponent({
  __name: 'Account',
  setup(__props) {

  const router = useRouter()

  const { checkedCircle, checkmark } = useIcons()
  const { trackPage, pageViewed, userInfoUpdated, resetToursAndHints, accountDeleted, emailVerificationRequested } =
    useSegment()
  const { onAnswerChoiceClick, setPhoneOrTextAnswer, profileQuestionIsUnfilled, triggerToursAndHints, closeModal } =
    useCommonMixin()
  const { showTourAfterFirstMarch } = useToursAndHints()
  const { requestResetPassword, requestingResetPassword } = useAuthentication()
  const { translateString } = useI18n()

  const formUser: { firstName: string; lastName: string } = reactive({
    firstName: '',
    lastName: '',
  })
  const email = ref('')
  const isApplyingChanges = ref(false)
  const isApplyingProfilePageChanges = ref(false)
  const sendingVerificationRequest = ref(false)
  const companyProfileQuestions = ref([] as CompanyProfileQuestion[])
  const selectedAnswers = ref([] as QuestionAnswer[])
  const mandatoryQuestionIds = ref([] as string[])
  const canResetTours = ref(false)

  const auth0Id = computed((): string | null => {
    return UserModule.auth0Id
  })

  const emailIsVerified = computed((): boolean => {
    return UserModule.emailVerified
  })

  const allRoutes = computed((): RouteRecordPublic[] => {
    return router.getRoutes()
  })

  const structuredCompanyProfileQuestions = computed(() => {
    return companyProfileQuestions.value?.map(
      (companyProfileQuestion): ExtendedProfileQuestion => ({
        position: companyProfileQuestion.position,
        mandatory: companyProfileQuestion.mandatory,
        ...companyProfileQuestion.profileQuestion,
      }),
    )
  })

  const isB2bUser = computed((): boolean => !!UserModule.currentCompany?.isB2b)

  const isDirectSignup = computed(() => {
    return !!auth0Id.value?.startsWith('auth0')
  })

  const emailVerifiedStatus = computed(() =>
    emailIsVerified.value
      ? `(${translateString('account.emailVerified')})`
      : `(${translateString('account.emailNotVerified')})`,
  )

  const mandatoryQuestionsFilled = computed(() => {
    return mandatoryQuestionIds.value.every((qId) => {
      const profileQuestion = structuredCompanyProfileQuestions.value.find((_q) => _q.id === qId)
      return !profileQuestionIsUnfilled.value(selectedAnswers.value, profileQuestion!)
    })
  })

  const prefillPersonalData = () => {
    formUser.firstName = UserModule.firstName
    formUser.lastName = UserModule.lastName
    email.value = UserModule.email
  }

  const fetchProfileQuestions = () => {
    UsersApi.getUserProfileQuestions()
      .then((questions) => {
        companyProfileQuestions.value = questions
      })
      .finally(() => {
        setSelectedAnswers(structuredCompanyProfileQuestions.value)
        setMandatoryQuestions()
      })
  }

  const setSelectedAnswers = (questions: StructuredCompanyProfileQuestion[]) => {
    questions.forEach((question) => {
      if (question.type === 'text') {
        setAnswer(question.id, question.textAnswer ?? '', 'text')
      } else if (question.type === 'phone_input') {
        setAnswer(question.id, question.phoneAnswer ?? '', 'phone')
      } else {
        question.answers.forEach((answer) => {
          if (answer.selected) {
            onAnswerChoiceClick(
              selectedAnswers.value,
              answer.id,
              question.id,
              question.type === 'dropdown' ? question.type : undefined,
            )
          }
        })
      }
    })
  }

  const setMandatoryQuestions = () => {
    mandatoryQuestionIds.value =
      structuredCompanyProfileQuestions.value?.filter((q) => q.mandatory).map((_q) => _q.id) ?? []
  }

  const setAnswer = (questionId: string, text: string, answerType: 'phone' | 'text') => {
    setPhoneOrTextAnswer(selectedAnswers.value, questionId, text, answerType)
  }

  const triggerResetPassword = () => {
    if (!emailIsVerified.value) {
      eventBus.$toasted.info(translateString('account.verifyYourEmail'))
    } else {
      requestResetPassword(email.value)
    }
  }

  const applyChanges = () => {
    if (formUser.firstName !== UserModule.firstName || formUser.lastName !== UserModule.lastName) {
      isApplyingChanges.value = true
      UsersApi.updatePersonalData({
        auth0Id: UserModule.auth0Id,
        firstName: formUser.firstName,
        lastName: formUser.lastName,
      }).finally(() => {
        userInfoUpdated!()
        isApplyingChanges.value = false
        eventBus.$toasted.success(translateString('account.changesApplied'))
      })
    }
  }

  const applyProfilePageChanges = async () => {
    isApplyingProfilePageChanges.value = true
    UsersApi.updateUserProfileChoices(selectedAnswers.value)
      .then((questions) => {
        companyProfileQuestions.value = questions
        eventBus.$toasted.success(translateString('account.updatedQuestions'), { duration: 5000 })
      })
      .finally(() => {
        isApplyingProfilePageChanges.value = false
      })
  }

  const sendEmailVerification = () => {
    sendingVerificationRequest.value = true
    UsersApi.sendEmailVerification(UserModule.auth0Id)
      .then((status) => {
        if (status === 201) {
          emailVerificationRequested(UserModule.email)
          eventBus.$toasted.success(translateString('account.emailSent'), { duration: 5000 })
        }
      })
      .finally(() => {
        sendingVerificationRequest.value = false
      })
  }

  const onResetToursAndHints = () => {
    allRoutes.value.forEach((route) => {
      const pageTour = SEEN_PAGE_TOUR.replace('{route_name}', route.name?.toUpperCase() as string)
      const pageHint = CLOSED_PAGE_HINT.replace('{route_name}', route.name?.toUpperCase() as string)
      CookieHelper.removeCookie(`${pageTour}`)
      CookieHelper.removeCookie(`${pageHint}`)
    })

    eventBus.$emit('set-tours-and-hints')
    resetToursAndHints()
    eventBus.$toasted.success(translateString('account.resetToursConfirmation'), { duration: 5000 })
  }

  const deleteAccount = () => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'ConfirmationModal',
      modalTitle: translateString('account.accountDeletion'),
      modalProps: {
        confirmationText: translateString('account.confirmationModalText'),
        okCallback: closeConfirmationAndDeleteAccount,
        cancelButtonText: translateString('cancel'),
      },
      cssClass: 'confirmation',
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  const closeConfirmationAndDeleteAccount = () => {
    UsersApi.archiveUser().finally(() => {
      accountDeleted()
      closeModal()
      router.push('/logout')
    })
  }

  watch(auth0Id, () => {
    prefillPersonalData()
  })

  prefillPersonalData()
  fetchProfileQuestions()

  onMounted(() => {
    trackPage('Account')
    pageViewed('Account')
    showTourAfterFirstMarch(() => (canResetTours.value = true))
  })

return { __sfc: true,router, checkedCircle, checkmark, trackPage, pageViewed, userInfoUpdated, resetToursAndHints, accountDeleted, emailVerificationRequested, onAnswerChoiceClick, setPhoneOrTextAnswer, profileQuestionIsUnfilled, triggerToursAndHints, closeModal, showTourAfterFirstMarch, requestResetPassword, requestingResetPassword, translateString, formUser, email, isApplyingChanges, isApplyingProfilePageChanges, sendingVerificationRequest, companyProfileQuestions, selectedAnswers, mandatoryQuestionIds, canResetTours, auth0Id, emailIsVerified, allRoutes, structuredCompanyProfileQuestions, isB2bUser, isDirectSignup, emailVerifiedStatus, mandatoryQuestionsFilled, prefillPersonalData, fetchProfileQuestions, setSelectedAnswers, setMandatoryQuestions, setAnswer, triggerResetPassword, applyChanges, applyProfilePageChanges, sendEmailVerification, onResetToursAndHints, deleteAccount, closeConfirmationAndDeleteAccount, computed, onMounted, reactive, ref, watch, KetchUpButton, LoadingSpinner, SVGRenderer, ProfileQuestion, useRouter, useIcons, useSegment, useCommonMixin, useAuthentication, useToursAndHints, useI18n, UsersApi, UserModule, CookieHelper, SEEN_PAGE_TOUR, CLOSED_PAGE_HINT, eventBus }
}

})